import { Stack } from '@mui/material';
import React, { useState } from 'react';

import { LocationMergeAccordianFragmentProps } from './LocationMergeAccordianFragment.props';
import { StyledSelectMenu } from './LocationMergeAccordianFragment.styles';
import { StructureMergeDecisionData, StructureMergeLocationNodeData } from '../../../../@types';
import { Button, ButtonColorProps } from '../../../../components/atoms/Button';
import { Panel } from '../../../../components/atoms/Panel';
import { Typography } from '../../../../components/atoms/Typography';
import { LocationMergeLiveSearch } from '../../../../components/molecules/LocationMergeLiveSearch';
import { COLORS } from '../../../../configs/colors';
import {
  LOCATION_MERGE_DROPDOWN_ITEMS,
  LOCATION_MERGE_WARNING_MESSAGE,
  STRUCTUE_MERGE_DECISION_SAMPLE_DATA,
  STRUCTURE_MERGE_LOCATION_NODE_SAMPLE_DATA,
} from '../../../../constants/structure';
import { useMergeStructure } from '../../../../queries/structure-query';
import { hasErrorInMerge } from '../../../../utils/structure';
import { toast } from '../../../../utils/toast';

export const LocationMergeAccordianFragment: React.FC<LocationMergeAccordianFragmentProps> = ({
  mergeNodeId,
  nodeTypeCode,
}) => {
  const [isCancel, setIsCancel] = useState<boolean>(false);
  const [location, setLocation] = useState<StructureMergeLocationNodeData>(
    STRUCTURE_MERGE_LOCATION_NODE_SAMPLE_DATA
  );
  const [locationMergeDecision, setLocationMergeDecision] = useState<StructureMergeDecisionData>(
    STRUCTUE_MERGE_DECISION_SAMPLE_DATA
  );
  const [validate, setValidate] = useState(false);

  const isError = hasErrorInMerge(
    location,
    locationMergeDecision.addressesDecision,
    locationMergeDecision.contactsDecision,
    locationMergeDecision.tradingHoursDecision
  );

  const mergeStructure = useMergeStructure();

  const handleCancel = () => {
    setLocation(STRUCTURE_MERGE_LOCATION_NODE_SAMPLE_DATA);
    setLocationMergeDecision(STRUCTUE_MERGE_DECISION_SAMPLE_DATA);
    setIsCancel(true);
  };

  const handleSave = async () => {
    locationMergeDecision.mergeNodeId = mergeNodeId;
    locationMergeDecision.currentNodeId = location.id;

    if (isError) {
      setValidate(true);
      return;
    }

    await mergeStructure.mutateAsync(locationMergeDecision);

    // The Snackbar that used to confirm this went with the toast migration and
    // nothing replaced it, so a merge — which is destructive and not obviously
    // reflected on screen — completed silently.
    toast.success('Structure merged');

    setLocation(STRUCTURE_MERGE_LOCATION_NODE_SAMPLE_DATA);
    setLocationMergeDecision(STRUCTUE_MERGE_DECISION_SAMPLE_DATA);
    setIsCancel(true);
  };

  return (
    // Open on arrival: it is the only panel on the Administration tab, so
    // arriving there to find one shut bar is a click that asks nothing and
    // tells nothing. Still collapsible, so it can be folded away while reading
    // the confirmation.
    <Panel title="Location merge" collapsible defaultExpanded>
      <Stack direction="column" alignItems="center" spacing={2}>
        <LocationMergeLiveSearch
          mergeNodeId={mergeNodeId}
          nodeTypeCode={nodeTypeCode}
          onChange={setLocation}
          isCancel={isCancel}
          setCancel={setIsCancel}
        />
        {!!location?.id && (
          <Typography textColor={COLORS.Red}>{LOCATION_MERGE_WARNING_MESSAGE}</Typography>
        )}
        {location?.canHaveAddress && (
          <StyledSelectMenu
            required={true}
            id="addresses"
            labelId="addresses"
            label="Addresses"
            selectedValue={locationMergeDecision.addressesDecision}
            onChange={(val) => {
              setLocationMergeDecision({
                ...locationMergeDecision,
                addressesDecision: val,
              });
            }}
            items={LOCATION_MERGE_DROPDOWN_ITEMS}
            validate={validate}
            optionalLabelEnabled={true}
          />
        )}
        {location?.canHaveContact && (
          <StyledSelectMenu
            required={true}
            id="contacts"
            labelId="contacts"
            label="Contacts"
            selectedValue={locationMergeDecision.contactsDecision}
            onChange={(val) => {
              setLocationMergeDecision({
                ...locationMergeDecision,
                contactsDecision: val,
              });
            }}
            items={LOCATION_MERGE_DROPDOWN_ITEMS}
            validate={validate}
            optionalLabelEnabled={true}
          />
        )}
        {location?.canHaveTradingHours && (
          <StyledSelectMenu
            required={true}
            id="trading-hours"
            labelId="trading-hours"
            label="Trading Hours"
            selectedValue={locationMergeDecision.tradingHoursDecision}
            onChange={(val) => {
              setLocationMergeDecision({
                ...locationMergeDecision,
                tradingHoursDecision: val,
              });
            }}
            items={LOCATION_MERGE_DROPDOWN_ITEMS}
            validate={validate}
            optionalLabelEnabled={true}
          />
        )}
      </Stack>
      {!!location?.id && (
        <Stack direction="row" justifyContent="flex-end" alignItems="flex-end" spacing={2} mt={2}>
          <Button color={ButtonColorProps.Error} onClick={handleCancel}>
            Cancel
          </Button>
          <Button color={ButtonColorProps.Primary} onClick={handleSave}>
            Save
          </Button>
        </Stack>
      )}
    </Panel>
  );
};
